import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import {
  BEAD_PX,
  HINT_NUMBER,
  PRACTICE_NUMBER,
  SorobanColumnState,
  VirtualAbacusState,
  canvas2dAvailable,
  cleared,
  encodeNumber,
  moveEarth,
  placeLabel,
  snapshot,
  toggleHeaven,
  valueOf,
} from './soroban';

interface VirtualAbacusProps {
  columns?: number;
  onStateChange?: (state: VirtualAbacusState) => void;
}

const beadStyle = {
  minWidth: BEAD_PX,
  minHeight: BEAD_PX,
  width: BEAD_PX,
  height: BEAD_PX,
  padding: 0,
} as const;

const RodDiagram: React.FC<{ columns: SorobanColumnState[] }> = ({ columns }) => (
  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }} aria-hidden>
    {columns.map((column, columnIndex) => (
      <Box key={`diagram-${columnIndex}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            width: BEAD_PX,
            height: BEAD_PX,
            borderRadius: '50%',
            bgcolor: column.upperActive ? 'error.main' : 'grey.400',
            mb: column.upperActive ? 0 : 2,
          }}
        />
        <Box sx={{ width: BEAD_PX + 8, height: 4, bgcolor: 'text.primary', my: 1 }} />
        {[0, 1, 2, 3].map((beadIndex) => (
          <Box
            key={`diagram-earth-${columnIndex}-${beadIndex}`}
            sx={{
              width: BEAD_PX,
              height: BEAD_PX,
              borderRadius: '24px',
              bgcolor: beadIndex < column.lowerActive ? 'primary.main' : 'grey.300',
              mt: beadIndex === column.lowerActive ? 2 : 0.5,
            }}
          />
        ))}
      </Box>
    ))}
  </Box>
);

/**
 * Standard soroban for a finger-theory lesson. Bead moves stay in the browser.
 * When a 2D canvas context is missing, a static diagram of the practice target is shown instead.
 */
const VirtualAbacus: React.FC<VirtualAbacusProps> = ({ columns = 2, onStateChange }) => {
  const columnCount = Math.max(2, columns);
  const [rods, setRods] = useState<SorobanColumnState[]>(() => cleared(columnCount));
  const [checkResult, setCheckResult] = useState<'correct' | 'incorrect' | null>(null);
  const interactive = useMemo(() => canvas2dAvailable(), []);
  const state = useMemo(() => snapshot(rods), [rods]);
  const skipColumnReset = useRef(true);

  useEffect(() => {
    if (skipColumnReset.current) {
      skipColumnReset.current = false;
      return;
    }
    setRods(cleared(columnCount));
    setCheckResult(null);
  }, [columnCount]);

  useEffect(() => {
    onStateChange?.(state);
  }, [onStateChange, state]);

  if (!interactive) {
    return (
      <Paper sx={{ p: 2 }} data-testid="abacus-static-diagram">
        <Typography variant="h6" fontWeight={700} mb={1}>
          Virtual soroban
        </Typography>
        <Box role="img" aria-label={`Diagram of ${PRACTICE_NUMBER}`}>
          <Typography variant="body1" mb={2}>
            Show {PRACTICE_NUMBER}
          </Typography>
          <RodDiagram columns={encodeNumber(PRACTICE_NUMBER, columnCount)} />
        </Box>
      </Paper>
    );
  }

  const apply = (next: SorobanColumnState[]) => {
    setRods(next);
    setCheckResult(null);
  };

  return (
    <Paper sx={{ p: 2, overflowX: 'auto' }} data-testid="virtual-abacus" data-abacus-state={JSON.stringify(state)}>
      <Typography variant="h6" fontWeight={700} mb={1}>
        Virtual soroban
      </Typography>
      <Typography variant="body1" mb={2}>
        Show {PRACTICE_NUMBER}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', pb: 1 }}>
        {rods.map((column, columnIndex) => {
          const place = columnCount - columnIndex - 1;
          const label = placeLabel(place);
          return (
            <Box
              key={`col-${columnIndex}`}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <Typography variant="caption" color="text.secondary" mb={1}>
                {label}
              </Typography>
              <Button
                aria-label={`Heaven bead, ${label}`}
                aria-pressed={column.upperActive}
                data-testid={`abacus-heaven-${columnIndex}`}
                onClick={() => apply(toggleHeaven(rods, columnIndex))}
                style={beadStyle}
                sx={{
                  borderRadius: '50%',
                  minWidth: BEAD_PX,
                  bgcolor: column.upperActive ? 'error.main' : 'grey.400',
                  mb: column.upperActive ? 0 : 2,
                  touchAction: 'manipulation',
                  '&:hover': { bgcolor: column.upperActive ? 'error.dark' : 'grey.500' },
                }}
              />
              <Box sx={{ width: BEAD_PX + 8, height: 4, bgcolor: 'text.primary', my: 1 }} />
              {[0, 1, 2, 3].map((beadIndex) => {
                const active = beadIndex < column.lowerActive;
                return (
                  <Button
                    key={`earth-${columnIndex}-${beadIndex}`}
                    aria-label={`Earth bead ${beadIndex + 1}, ${label}`}
                    aria-pressed={active}
                    data-testid={`abacus-earth-${columnIndex}-${beadIndex}`}
                    onClick={() => apply(moveEarth(rods, columnIndex, beadIndex))}
                    style={beadStyle}
                    sx={{
                      borderRadius: '24px',
                      minWidth: BEAD_PX,
                      bgcolor: active ? 'primary.main' : 'grey.300',
                      color: 'transparent',
                      mt: beadIndex === column.lowerActive ? 2 : 0.5,
                      touchAction: 'manipulation',
                      '&:hover': { bgcolor: active ? 'primary.dark' : 'grey.400' },
                    }}
                  />
                );
              })}
            </Box>
          );
        })}
      </Box>

      <Typography variant="body2" data-testid="abacus-value" sx={{ mt: 2 }}>
        Beads show {valueOf(rods)}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
        <Button variant="outlined" onClick={() => apply(encodeNumber(HINT_NUMBER, columnCount))}>
          Set {HINT_NUMBER}
        </Button>
        <Button
          variant="contained"
          data-testid="abacus-check"
          onClick={() => setCheckResult(valueOf(rods) === PRACTICE_NUMBER ? 'correct' : 'incorrect')}
        >
          Check
        </Button>
      </Stack>
      {checkResult && (
        <Typography
          data-testid="abacus-check-result"
          sx={{ mt: 1 }}
          color={checkResult === 'correct' ? 'success.main' : 'warning.main'}
        >
          {checkResult === 'correct' ? 'Correct' : 'Not a match'}
        </Typography>
      )}
    </Paper>
  );
};

export default VirtualAbacus;
